import React, { useState } from 'react';
import { TableCell, IconButton, Box, ClickAwayListener } from '@mui/material';
import { IconHot } from 'components/common/IconHot';
import { TextHot } from 'components/common/TextHot';
import { StatusBadge } from './StatusBadge';
import { colors, spacing } from 'common/theme';
import TrashIconSvg from 'assets/personal_zone/Trash_Outline.svg';
import ArchiveIconSvg from 'assets/personal_zone/Archive.svg';
import ExtractArchiveIconSvg from 'assets/personal_zone/Extract_Archive.svg';
import CopyIconSvg from 'assets/personal_zone/Copy.svg';
import { TooltipHot } from 'components/common/TooltipHot';
import { COUPON_STATUS } from '../../utils/enums';
import HelpSharpIcon from '@mui/icons-material/HelpSharp';
import { TOOLTIP_MODE, TOOLTIP_PLACEMENT } from '../../utils/enums';
import { ButtonHot, BUTTON_MODE, BUTTON_VARIANT } from 'components/common/ButtonHot';
import { generateTestId } from 'app/functions';
import { useTranslations } from 'common/hooks/useTranslations';
import { Link } from 'react-router-dom';
const CELL_STYLE = {
    border: 'none',
    verticalAlign: 'middle',
    paddingY: `${spacing.xl}px`,
    paddingLeft: 0,
    paddingRight: 0,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
};

function CouponTableRow({ 
    benefit, 
    coupon, 
    isMultipassCoupon, 
    testID, 
    actionsBaseTestID: externalActionsBaseTestID,
    onArchive, 
    onCancel, 
    benefitId, 
    isPaidCoupons = true 
}) {
    const [copyTooltipOpen, setCopyTooltipOpen] = useState(false);
    const [copyHoverOpen, setCopyHoverOpen] = useState(false);
    const [viewBudgetTooltipOpen, setViewBudgetTooltipOpen] = useState(false);
    const [currentBudget, setCurrentBudget] = useState(0);
    const [cancelTooltipOpen, setCancelTooltipOpen] = useState(false);
    const [cancelHoverOpen, setCancelHoverOpen] = useState(false);
    const [budgetHoverOpen, setBudgetHoverOpen] = useState(false);
    const [archiveTooltipOpen, setArchiveTooltipOpen] = useState(false);
    const { translate } = useTranslations();
    const baseTestID = testID || 'coupon-table-row';
    const rowTestID = generateTestId(baseTestID, `coupon-row-${coupon.code || coupon.id}`);
    const actionsBaseTestID = externalActionsBaseTestID || generateTestId(baseTestID, 'actions');

    const handleCancelClick = (e) => {
        if (e && e.stopPropagation) e.stopPropagation();

        setCancelTooltipOpen(false);

        if (onCancel) {
            onCancel(coupon, benefitId, e);
        }
    };

    const handleCopyClick = async (e) => {
        e.stopPropagation();
        setCopyHoverOpen(false);
        try {
            await navigator.clipboard?.writeText(coupon.code);
            setCopyTooltipOpen(true);
            setTimeout(() => setCopyTooltipOpen(false), 2000);
        } catch (error) {
            console.error('Failed to copy:', error);
        }
    };

    const handleViewBudgetClick = (e) => {
        e.stopPropagation();
        setBudgetHoverOpen(false);
        if (benefit.multipassType !== 0) {
            setCurrentBudget(coupon.currentBudget || coupon.originalPrice);
        }
        setViewBudgetTooltipOpen(true);
        setTimeout(() => setViewBudgetTooltipOpen(false), 2000);
    };

    const cancelTooltipContent = (
        <Box 
            testId={generateTestId(actionsBaseTestID, 'cancel-tooltip-content')}
            onClick={(e) => e.stopPropagation()}
            sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: spacing.xs,
                maxWidth: '280px',
                p: 1,
                textAlign: 'left',
                alignItems: 'flex-start'
            }}
        >
            <TextHot 
                testID={generateTestId(actionsBaseTestID, 'cancel-tooltip-text')}
                size="sm" 
                color="primary" 
                sx={{ lineHeight: 1.5 }} 
                isLtr={true}
            >
               {translate('modal_disclaimer_text')}
            </TextHot>
            <ButtonHot
                testID={generateTestId(actionsBaseTestID, 'cancel-confirm-btn')}
                mode={BUTTON_MODE.CONTAINED}
                variant={BUTTON_VARIANT.PRIMARY}
                size="s"
                fullWidth={false}
                onClick={(e) => {
                    handleCancelClick(e);
                    setCancelTooltipOpen(false);
                }}
                onMouseDown={handleCancelClick}
                sx={{ 
                    px: 3,
                    alignSelf: 'flex-start'
                }}
            >
                {translate('cancel_modal_button_text')}
            </ButtonHot>
        </Box>
    );

    const shouldShowCursor = benefit?.shouldRedirectToBenefitScreen;
    const cursorStyle = shouldShowCursor ? { cursor: 'pointer' } : {};

    return (
        <>
            <TableCell testId={generateTestId(rowTestID, 'actions-cell')} sx={{ ...CELL_STYLE, px: 0, ...cursorStyle }}>
                <Box testId={generateTestId(rowTestID, 'actions-container')} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, direction: 'rtl' }}>
                    
                    {(isMultipassCoupon && isPaidCoupons && (coupon.status === COUPON_STATUS.SUCCESS || coupon.status === COUPON_STATUS.PARTIALLY_USED)) && (
                        <TooltipHot
                            testID={generateTestId(actionsBaseTestID, 'budget-hover-tooltip')}
                            title={translate('left_to_use_hover_tooltip_label')}
                            open={budgetHoverOpen && !viewBudgetTooltipOpen}
                            onOpen={() => setBudgetHoverOpen(true)}
                            onClose={() => setBudgetHoverOpen(false)}
                            disableHoverListener={viewBudgetTooltipOpen}
                            disableFocusListener={viewBudgetTooltipOpen}
                        >
                            <span style={{ display: 'inline-flex' }}>
                                <TooltipHot
                                    testID={generateTestId(actionsBaseTestID, 'budget-tooltip')}
                                    title={translate('left_to_use_tooltip_label', { currentBudget })}
                                    open={viewBudgetTooltipOpen}
                                    disableHoverListener
                                    disableFocusListener
                                >
                                    <IconButton
                                        testId={generateTestId(actionsBaseTestID, 'budget-btn')}
                                        size="small"
                                        onClick={handleViewBudgetClick}
                                        sx={{
                                            color: colors.system.text.subtle,
                                            p: 0
                                        }}
                                    >
                                        <IconHot src={HelpSharpIcon} size={spacing.xl} />
                                    </IconButton>
                                </TooltipHot>
                            </span>
                        </TooltipHot>
                    )}

                    {(isPaidCoupons && coupon.status === COUPON_STATUS.SUCCESS) && (
                        <TooltipHot
                            testID={generateTestId(actionsBaseTestID, 'cancel-hover-tooltip')}
                            title={translate('cancel_coupon_hover_tooltip_title')}
                            open={cancelHoverOpen && !cancelTooltipOpen}
                            onOpen={() => setCancelHoverOpen(true)}
                            onClose={() => setCancelHoverOpen(false)}
                            disableHoverListener={cancelTooltipOpen}
                            disableFocusListener={cancelTooltipOpen}
                        >
                            <span style={{ display: 'inline-flex' }}>
                                <ClickAwayListener onClickAway={() => setCancelTooltipOpen(false)}>
                                    <span style={{ display: 'inline-flex' }}>
                                        <TooltipHot
                                            testID={generateTestId(actionsBaseTestID, 'cancel-click-tooltip')}
                                            title={cancelTooltipContent}
                                            mode={TOOLTIP_MODE.LIGHT}
                                            placement={TOOLTIP_PLACEMENT.BOTTOM}
                                            open={cancelTooltipOpen}
                                            onClose={() => setCancelTooltipOpen(false)}
                                            leaveDelay={0}
                                            interactive
                                            disableHoverListener
                                            disableFocusListener
                                        >
                                            <IconButton
                                                testId={generateTestId(actionsBaseTestID, 'cancel-btn')}
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setCancelHoverOpen(false);
                                                    setCancelTooltipOpen(true);
                                                }}
                                                sx={{
                                                    color: colors.system.text.subtle,
                                                    p: 0
                                                }}
                                            >
                                                <IconHot src={TrashIconSvg} size={spacing.xl} />
                                            </IconButton>
                                        </TooltipHot>
                                    </span>
                                </ClickAwayListener>
                            </span>
                        </TooltipHot>
                    )}

                    {isPaidCoupons && (
                        <TooltipHot 
                            testID={generateTestId(actionsBaseTestID, 'archive-tooltip')}
                            title={coupon.is_archived ? translate('unarchive_coupon_hover_tooltip_title') : translate('archive_coupon_hover_tooltip_title')}
                            open={archiveTooltipOpen}
                            onOpen={() => setArchiveTooltipOpen(true)}
                            onClose={() => setArchiveTooltipOpen(false)}
                        >
                            <IconButton 
                                testId={generateTestId(actionsBaseTestID, 'archive-btn')}
                                size="small" 
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setArchiveTooltipOpen(false);
                                    onArchive?.(coupon, benefitId);
                                }} 
                                sx={{ color: colors.system.text.subtle, p: 0 }}
                            >
                                <IconHot src={coupon.is_archived ? ExtractArchiveIconSvg : ArchiveIconSvg} size={spacing.xl} />
                            </IconButton>
                        </TooltipHot>
                    )}

                    <TooltipHot
                        testID={generateTestId(actionsBaseTestID, 'copy-hover-tooltip')}
                        title={translate('copy_coupon_hover_tooltip_title')}
                        open={copyHoverOpen && !copyTooltipOpen}
                        onOpen={() => setCopyHoverOpen(true)}
                        onClose={() => setCopyHoverOpen(false)}
                        disableHoverListener={copyTooltipOpen}
                        disableFocusListener={copyTooltipOpen}
                    >
                        <span style={{ display: 'inline-flex' }}>
                            <TooltipHot
                                testID={generateTestId(actionsBaseTestID, 'copy-success-tooltip')}
                                title={translate('coupon_code_copied')}
                                open={copyTooltipOpen}
                                disableHoverListener
                                disableFocusListener
                            >
                                <IconButton
                                    testId={generateTestId(actionsBaseTestID, 'copy-btn')}
                                    size="small"
                                    onClick={handleCopyClick}
                                    sx={{ p: 0 }}
                                >
                                    <IconHot src={CopyIconSvg} size={spacing.xl} />
                                </IconButton>
                            </TooltipHot>
                        </span>
                    </TooltipHot>
                </Box>
            </TableCell>

            <TableCell testId={generateTestId(rowTestID, 'code-cell')} sx={{ border: 'none', maxWidth: '200px', ...cursorStyle }}>
                {coupon.isExternalLinkCoupon ? (
                    <a href={coupon.code} target="_blank">
                        <TextHot 
                            testID={generateTestId(rowTestID, 'code')}
                            size="xl"
                            bold="bold"
                            color="primary"
                            sx={{ display: 'block', lineHeight: 1.2, wordBreak: 'break-word' }}
                        >
                            {coupon.code}
                        </TextHot>
                    </a>
                ) : (
                    <TextHot 
                        testID={generateTestId(rowTestID, 'code')}
                        size="xl"
                        bold="bold"
                        color="primary"
                        sx={{ display: 'block', lineHeight: 1.2, wordBreak: 'break-word' }}
                    >
                        {coupon.code}
                    </TextHot>
                )}
            </TableCell>

            <TableCell testId={generateTestId(rowTestID, 'status-cell')} sx={{ border: 'none', ...cursorStyle }}>
                <Box testId={generateTestId(rowTestID, 'status-container')} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <StatusBadge testID={generateTestId(rowTestID, 'status-badge')} status={coupon.status} />
                </Box>
            </TableCell>

            <TableCell testId={generateTestId(rowTestID, 'validity-cell')} sx={{ border: 'none', ...cursorStyle }}>
                <TextHot 
                    testID={generateTestId(rowTestID, 'validity')}
                    size="xl" 
                    color="primary" 
                    sx={{ display: 'block', lineHeight: 1.2 }}
                >
                    {coupon.validity}
                </TextHot>
            </TableCell>

            {isPaidCoupons && (
                <TableCell testId={generateTestId(rowTestID, 'price-cell')} sx={{ border: 'none', ...cursorStyle }}>
                    <Box testId={generateTestId(rowTestID, 'price-container')} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextHot testID={generateTestId(rowTestID, 'original-price-text')} size="xl" color="subtle" sx={{ lineHeight: 1.2 }}>
                            <span style={{ textDecoration: 'line-through', lineHeight: 1.2 }}>₪{coupon.originalPrice}</span>
                        </TextHot>
                        <TextHot testID={generateTestId(rowTestID, 'price')} size="xl" color="subtle" sx={{ lineHeight: 1.2 }}>
                            ₪{coupon.price}
                        </TextHot>
                    </Box>
                </TableCell>
            )}

            <TableCell testId={generateTestId(rowTestID, 'order-date-cell')} sx={{ border: 'none', ...cursorStyle }}>
                <TextHot 
                    testID={generateTestId(rowTestID, 'order-date')}
                    size="xl" 
                    color="primary" 
                    sx={{ display: 'block', lineHeight: 1.2 }}
                >
                    {coupon.orderDate}
                </TextHot>
            </TableCell>

            <TableCell testId={generateTestId(rowTestID, 'order-number-cell')} sx={{ border: 'none', ...cursorStyle }}>
                <TextHot 
                    testID={generateTestId(rowTestID, 'order-number')}
                    size="xl" 
                    color="primary" 
                    sx={{ display: 'block', lineHeight: 1.2 }}
                >
                    {coupon.orderNumber}
                </TextHot>
            </TableCell>
        </>
    );
}

export default CouponTableRow;